import { useEffect } from 'react';
import { api } from '../lib/api';
import { IDLE_MS, activityExpired, activityChannel } from '../lib/idleSession';

export const useIdleSession = (sessionId, idleExpiresAt, expire) => {
  useEffect(() => {
    if (!sessionId || !idleExpiresAt) return;
    let record = { session: sessionId, at: Date.parse(idleExpiresAt) - IDLE_MS };
    let stopped = false, trailing, lastInput = 0, lastPing = 0;
    let anchor = performance.now() - Math.max(0, Date.now() - record.at);
    const channel = activityChannel(latest => {
      if (latest?.session !== sessionId || !Number.isFinite(latest.at) || latest.at <= record.at || latest.at > Date.now() + 30000) return;
      record = latest; anchor = performance.now() - Math.max(0, Date.now() - record.at);
    });
    const check = () => {
      if (stopped) return false;
      if (activityExpired(record) || performance.now() - anchor >= IDLE_MS) {
        stopped = true; clearTimeout(trailing); expire('idle'); return false;
      }
      return true;
    };
    const ping = () => {
      if (!check()) return;
      lastPing = Date.now();
      api.post('/auth/activity').catch(error => {
        if (error.response?.status !== 401) console.warn('Session activity could not be confirmed.');
      });
    };
    const input = event => {
      if (!event.isTrusted || !check() || Date.now() - lastInput < 500) return;
      lastInput = Date.now(); anchor = performance.now(); record = { session: sessionId, at: lastInput };
      channel.publish(record);
      if (Date.now() - lastPing >= 30000) ping();
      clearTimeout(trailing); trailing = setTimeout(ping, 1500);
    };
    const events = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];
    events.forEach(event => window.addEventListener(event, input, { passive: true }));
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    const timer = setInterval(check, 1000);
    check();
    return () => {
      stopped = true; channel.close(); clearInterval(timer); clearTimeout(trailing);
      events.forEach(event => window.removeEventListener(event, input));
      window.removeEventListener('focus', check); document.removeEventListener('visibilitychange', check);
    };
  }, [sessionId, idleExpiresAt, expire]);
};